import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { LayoutTemplate, Plus, Trash2, Globe, ChevronRight, Search, X, Blocks } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { Pagination } from '../components/ui/Pagination.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CmsSpace {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  type: string;
  createdAt: string;
  updatedAt: string;
}

interface SpaceForm {
  name: string;
  slug: string;
  type: string;
  domain: string;
}

const EMPTY_FORM: SpaceForm = {
  name: '',
  slug: '',
  type: 'website',
  domain: '',
};

const SPACE_TYPES = ['website', 'landing', 'app'] as const;

const PAGE_SIZE = 25;

function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsSpacesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CmsSpace | null>(null);
  const [form, setForm] = useState<SpaceForm>(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data = [], isLoading } = useQuery<CmsSpace[]>({
    queryKey: ['cms-spaces'],
    queryFn: () => api.get('/api/v1/cms/spaces').then((r) => r.data),
  });

  const filteredSpaces = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return data;
    return data.filter(
      (s) => s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q),
    );
  }, [data, search]);

  const totalPages = Math.ceil(filteredSpaces.length / PAGE_SIZE);
  const pagedSpaces = filteredSpaces.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const createMutation = useMutation({
    mutationFn: (body: object) => api.post('/api/v1/cms/spaces', body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['cms-spaces'] });
      setShowCreate(false);
      setForm(EMPTY_FORM);
      toast.success('Space created');
      navigate(`/spaces/${(res.data as CmsSpace).id}`);
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create space')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/cms/spaces/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-spaces'] });
      setDeleteTarget(null);
      toast.success('Space deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function openCreate() {
    setForm(EMPTY_FORM);
    setShowCreate(true);
  }

  function handleCreate() {
    if (!form.name || !form.slug) return;
    createMutation.mutate({
      name: form.name,
      slug: form.slug,
      type: form.type,
      ...(form.domain ? { domain: form.domain } : {}),
    });
  }

  const typeColors: Record<string, 'default' | 'info' | 'success'> = {
    website: 'success',
    landing: 'info',
    app: 'default',
  };

  return (
    <div>
      <PageHeader
        title="Spaces"
        description="Spaces are top-level containers for pages, collections, nav, and redirects"
        action={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/cms/block-defs')}>
              <Blocks className="w-3.5 h-3.5 mr-1.5" /> Block Defs
            </Button>
            <Button onClick={openCreate} size="sm">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Space
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={3} cols={5} />
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={<LayoutTemplate size={26} />}
          title="No spaces yet"
          description="Create a space to start building pages and collections for the customer portal"
          action={{ label: 'New Space', onClick: openCreate }}
        />
      ) : (
        <>
          {/* Search toolbar */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search spaces…"
                className="h-9 pl-9 pr-8 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 w-56"
              />
              {search && (
                <button onClick={() => { setSearch(''); setPage(1); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-zinc-500">
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Slug
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Domain
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {pagedSpaces.map((space) => (
                  <tr
                    key={space.id}
                    className="hover:bg-zinc-50 transition-colors group cursor-pointer"
                    onClick={() => navigate(`/spaces/${space.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-zinc-900">{space.name}</td>
                    <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{space.slug}</td>
                    <td className="px-4 py-3">
                      <Badge variant={typeColors[space.type] ?? 'default'}>{space.type}</Badge>
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">
                      {space.domain ? (
                        <span className="flex items-center gap-1">
                          <Globe size={12} />
                          {space.domain}
                        </span>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">{fmt(space.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(space);
                          }}
                          className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-md"
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                        <ChevronRight size={13} className="text-zinc-300" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          )}
        </>
      )}

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setForm(EMPTY_FORM);
        }}
        title="New Space"
      >
        <div className="space-y-4">
          <Input
            label="Name *"
            value={form.name}
            onChange={(e) => {
              const name = e.target.value;
              setForm((f) => ({ ...f, name, slug: slugify(name) }));
            }}
            placeholder="Main Website"
          />
          <div>
            <Input
              label="Slug *"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="main-website"
              className="font-mono text-xs"
            />
            <p className="text-xs text-zinc-400 mt-1">Used in API and x-space-id header</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Type</label>
            <select
              className="w-full border border-zinc-200 rounded-lg text-sm px-3 h-9 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            >
              {SPACE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Custom domain"
            value={form.domain}
            onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
            placeholder="www.example.com (optional)"
          />
        </div>
        {createMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(createMutation.error)}</p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button
            loading={createMutation.isPending}
            disabled={!form.name || !form.slug}
            onClick={handleCreate}
          >
            Create Space
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete space?"
        message={`"${deleteTarget?.name}" and all its pages, collections, nav items, and redirects will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
